import { useState } from "react";

import type { UseQueryOptions } from "@tanstack/react-query";

import ParseEventStreamService from "app/pages/sales-intelligence/modules/ai-agent/services/streamParser/streamParserService";
import type { MessageType } from "app/pages/sales-intelligence/modules/ai-agent/services/streamParser/types";
import { quotaQueryClient } from "app/pages/sales-intelligence/modules/quota/queries/client";
import type { ResultWithFullResponse } from "app/services/fetchService";

import { aiAgentQueryKeys } from "./queryKeyFactory";
import { useSSEStream } from "./useSSEStream";

const useChatQuery = ({
    message,
    threadId,
    questionId,
    onComplete,
    fetcher,
    queryOptions,
}: {
    message?: string;
    threadId?: string;
    questionId: number;
    onComplete?: VoidFunction;
    fetcher: (payload: { message?: string; threadId?: string }) => Promise<ResultWithFullResponse<unknown>>;
    queryOptions?: Omit<UseQueryOptions, "queryKey" | "queryFn">;
}) => {
    const [data, setData] = useState("");
    const [messageId, setId] = useState("");
    const [messageType, setMessageType] = useState<MessageType>("ai");

    const handleComplete = () => {
        onComplete?.();
        setData("");
        void quotaQueryClient.invalidateDataCreditsState();
    };

    const { isFetching, stop, isFetched, isError, refetch } = useSSEStream({
        enabled: !!message && !!threadId,
        queryKey: aiAgentQueryKeys.chat(questionId.toString()),
        payload: { message, threadId },
        fetcher,
        onEvent: (event) => {
            if (ParseEventStreamService.isPartialMessageStreamEvent(event)) {
                setData((prev) => prev + event.data[0].content);
                if (event.data[0].id && event.data[0].id !== messageId) {
                    setId(event.data[0].id);
                }
                if (event.data[0].type && event.data[0].type !== messageType) {
                    setMessageType(event.data[0].type);
                }
            }
        },
        onComplete: handleComplete,
        queryOptions,
    });

    return { data, isLoading: isFetching, stop, messageId, isFetched, messageType, isError, refetch };
};

export default useChatQuery;
